import { Association, DocumentGroup } from "@/models";
import { apiRequest, SUPABASE_URL } from "./apiUtils";
import { AssociationProjectDetails } from "@/models/AssociationProjectDetails";
import { documentGroupApi } from "./documentApi";
import { createApiWrapper } from "./apiWrapper";

export interface AssociationSearchParams {
  search?: string;
  type?: string;
  limit?: number;
  offset?: number;
  management_company_id?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  metadata: {
    total_count: number;
    limit: number;
    offset: number;
  };
}

export interface BulkUploadResponse {
  success: boolean;
  message: string;
  total: number;
  imported: number;
  errors: string[];
}

// Create a base API wrapper with standard CRUD operations
const associationApiBase = createApiWrapper<Association>('associations');

/**
 * API client for association-related endpoints
 */
export const associationApi = {
  /**
   * List associations with optional filtering and pagination
   */
  list: async (params?: { queryKey?: any }): Promise<Association[] | PaginatedResponse<Association>> => {
    const [_key, searchParams = {}] = params?.queryKey || [];
    const queryParams = new URLSearchParams();

    if (searchParams.search) {
      queryParams.append('search', searchParams.search);
    }

    if (searchParams.type) {
      queryParams.append('type', searchParams.type);
    }

    const isPaginated = searchParams.limit !== undefined || searchParams.offset !== undefined;
    
    if (searchParams.limit) {
      queryParams.append('limit', searchParams.limit.toString());
    }

    if (searchParams.offset) {
      queryParams.append('offset', searchParams.offset.toString());
    }

    const url = `${SUPABASE_URL}/associations?${queryParams.toString()}`;
    console.log("Fetching associations from:", url);
    
    return apiRequest(url);
  },
  
  /**
   * Get a specific association by ID
   */
  getById: associationApiBase.getById,
  
  /**
   * Create a new association
   */
  create: associationApiBase.create,
  
  /**
   * Update an existing association
   */
  update: associationApiBase.update,
  
  /**
   * Delete an association
   */
  delete: associationApiBase.delete,
  
  /**
   * Get document groups for an association
   */
  getDocumentGroups: async (id: number) => 
    apiRequest<DocumentGroup[]>(`${SUPABASE_URL}/associations/${id}/document-groups`),
  
  /**
   * Update document groups for an association
   */
  updateDocumentGroups: async (id: number, documentGroupIds: number[]) => 
    apiRequest<Association>(`${SUPABASE_URL}/associations/${id}/document-groups`, {
      method: 'PUT',
      body: { document_groups: documentGroupIds }
    }),

  /**
   * Project Details: the lender questionnaire answered once per association.
   * An association that never answered comes back with empty answers.
   */
  getProjectDetails: async (id: number) =>
    apiRequest<AssociationProjectDetails>(`${SUPABASE_URL}/associations/${id}/project-details`),

  /**
   * Replaces the association's Project Details. Send everything: a key left
   * out is one the user cleared.
   */
  updateProjectDetails: async (id: number, answers: Record<string, string>) =>
    apiRequest<AssociationProjectDetails>(`${SUPABASE_URL}/associations/${id}/project-details`, {
      method: 'PUT',
      body: { answers }
    }),
    
  /**
   * Bulk upload associations from CSV file
   * @param file CSV file with association data
   * @param managementCompanyId ID of the management company
   * @returns Response with import results
   */
  bulkUpload: async (file: File, managementCompanyId: number) => {
    console.log(`Creating FormData with file: ${file.name} and managementCompanyId: ${managementCompanyId}`);
    
    const formData = new FormData();
    
    formData.append('file', file);
    formData.append('management_company_id', managementCompanyId.toString());
    
    console.log('FormData entries:', [...formData.entries()].map(entry => 
      entry[0] === 'file' ? `${entry[0]}: [File: ${(entry[1] as File).name}]` : `${entry[0]}: ${entry[1]}`
    ));
    
    const uploadUrl = `${SUPABASE_URL}/associations/import`;
    console.log('Sending bulk upload request to:', uploadUrl);
    
    try {
      return await apiRequest<BulkUploadResponse>(uploadUrl, {
        method: 'POST',
        body: formData,
      });
    } catch (error) {
      console.error("Error in bulkUpload:", error);
      throw error;
    }
  },

  /**
   * Export associations as CSV file
   * @param managementCompanyId ID of the management company
   * @returns CSV formatted string of association data
   */
  exportCsv: async (managementCompanyId: number): Promise<string> => {
    console.log(`Exporting associations CSV for management company ID: ${managementCompanyId}`);
    
    return apiRequest<string>(`${SUPABASE_URL}/associations/export-csv`, {
      method: 'POST',
      body: { management_company_id: managementCompanyId },
      responseType: 'text'
    });
  },

  /**
   * Get documents for an association
   * @param id Association ID
   * @returns Array of documents associated with the association
   */
  getDocuments: async (id: number) => {
    console.log(`Fetching documents for association ID: ${id}`);
    return apiRequest<any[]>(`${SUPABASE_URL}/associations/${id}/documents`);
  }
};
